/**
 * Header Component
 * 
 * Main application header providing navigation, search functionality, user
 * profile access, notifications, theme toggle, and language selection.
 * Dynamically displays page titles and search placeholders based on current route.
 */

import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '/styles/index.css';
import { useSearch } from '@/app/providers/SearchContext.jsx';
import { useTheme } from '@/app/providers/ThemeContext.jsx';
import { useTour } from '@/app/providers/TourContext.jsx';
import { useHeaderNotifications } from '@/shared/hooks/useHeaderNotifications.js';
import { useHeaderProfile } from '@/shared/hooks/useHeaderProfile.js';
import { getRouteConfig, deriveTitleFromPath, getUserFirstName } from '@/shared/utils/headerUtils.js';
import { tokenService } from '@/features/auth/services';
import { businessService } from '@/features/settings/services';
import { logger } from '@/shared/utils/logger.js';
import HelpSupportMenu from '@/shared/components/HelpSupportMenu.jsx';

const Header = ({
  title,
  subtitle,
  searchPlaceholder,
  onSearchChange
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { searchTerm, updateSearch, clearSearch } = useSearch();
  const { theme, effectiveTheme, updateTheme } = useTheme();
  const { startTour, getCurrentRouteTourId, isTourCompletedOrDismissed } = useTour();

  const [isOpen, setIsOpen] = useState(false);
  const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [dropdownTop, setDropdownTop] = useState(null);
  const [helpMenuTop, setHelpMenuTop] = useState(null);
  const dropdownRef = useRef(null);
  const helpMenuRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);
  const helpButtonRef = useRef(null);
  const [businessLogoUrl, setBusinessLogoUrl] = useState(null);
  const sidebarTimeoutRef = useRef(null);
  const overlayTimeoutRef = useRef(null);
  const authStateChangeTimeoutRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: '50%' });

  // Use custom hooks
  const {
    notifications: sortedNotifications,
    isLoadingNotifications,
    notificationError,
    notificationsUnreadCount: displayNotificationCount,
    actioningNotificationId,
    notificationActionType,
    isClearingNotifications,
    handleRefreshNotifications,
    handleMarkNotificationRead,
    handleDeleteNotification,
    handleClearNotifications,
  } = useHeaderNotifications(isOpen);

  const {
    userData,
    businessVerification,
    verificationLevel,
    verificationInfo,
    getVerificationLevelColors,
  } = useHeaderProfile();

  // Load business logo for business accounts (only once on mount or when userData changes)
  useEffect(() => {
    const loadBusinessLogo = async () => {
      try {
        const userType = userData?.usertype?.toLowerCase();
        const isBusinessAccount = userType === 'business_contact' || userType === 'business';

        if (!isBusinessAccount) {return;}

        // Try to get from localStorage first for quick display
        const cachedLogoUrl = localStorage.getItem('businessLogoUrl');
        if (cachedLogoUrl) {
          setBusinessLogoUrl(cachedLogoUrl);
        }

        // Fetch fresh logo from API (only once)
        const businessId = localStorage.getItem('businessId') ||
          userData?.businesses?.[0]?.id ||
          userData?.business?.id ||
          userData?.business_id ||
          userData?.businessId;

        if (!businessId) {return;}

        const response = await businessService.getBusinessLogo(businessId);
        if (response?.data?.logo_url || response?.logo_url) {
          const logoUrl = response?.data?.logo_url || response?.logo_url;
          setBusinessLogoUrl(logoUrl);
          localStorage.setItem('businessLogoUrl', logoUrl);
        }
      } catch (error) {
        // Logo not found or error - use fallback (user initials)
        logger.debug('No business logo available');
      }
    };

    if (userData) {
      loadBusinessLogo();
    }
  }, [userData]); // Removed businessLogoUrl from dependencies to prevent continuous API calls

  // Listen for logo updates from Profile page (separate effect to avoid re-fetching)
  useEffect(() => {
    const handleLogoUpdate = (event) => {
      const newLogoUrl = event?.detail?.logoUrl || localStorage.getItem('businessLogoUrl');
      if (newLogoUrl) {
        setBusinessLogoUrl(newLogoUrl);
      }
    };

    // Listen for custom event (same-tab updates from Profile page)
    window.addEventListener('businessLogoUpdated', handleLogoUpdate);
    
    return () => {
      window.removeEventListener('businessLogoUpdated', handleLogoUpdate);
    };
  }, []); // Empty dependency array - only set up listener once

  useEffect(() => {
    clearSearch();
  }, [location.pathname, clearSearch]);

  const routeConfig = getRouteConfig(t);
  const computedTitle = title || deriveTitleFromPath(location.pathname, t);
  const computedSearch = searchPlaceholder || (routeConfig[location.pathname]?.search || t('searchPlaceholder', { ns: 'common' }));

  const closeSidebar = () => {
    const sidebar = document.querySelector('.sidebar');
    const overlay = document.querySelector('.sidebar-overlay');

    // Clear any existing timeouts
    if (sidebarTimeoutRef.current) {
      clearTimeout(sidebarTimeoutRef.current);
      sidebarTimeoutRef.current = null;
    }
    if (overlayTimeoutRef.current) {
      clearTimeout(overlayTimeoutRef.current);
      overlayTimeoutRef.current = null;
    }

    if (sidebar) {
      sidebar.classList.remove('open');
      setIsSidebarOpen(false);

      // Clean up any inline styles that might prevent reopening
      sidebarTimeoutRef.current = setTimeout(() => {
        if (sidebar) {
          sidebar.style.transition = '';
          sidebar.style.left = '';
          sidebar.style.visibility = '';
        }
        sidebarTimeoutRef.current = null;
      }, 150);
    }

    if (overlay) {
      overlay.classList.remove('active');
      // Remove event listener if it exists
      if (overlay._clickHandler) {
        overlay.removeEventListener('click', overlay._clickHandler, true);
        overlay._clickHandler = null;
      }
      overlayTimeoutRef.current = setTimeout(() => {
        if (overlay && overlay.parentNode) {
          overlay.style.transition = '';
          overlay.style.opacity = '';
          overlay.style.visibility = '';
          overlay.remove();
        }
        overlayTimeoutRef.current = null;
      }, 150);
    }
  };

  // Clean up sidebar overlay on route change or unmount
  useEffect(() => {
    const cleanupOverlay = () => {
      const overlay = document.querySelector('.sidebar-overlay');
      if (overlay) {
        overlay.classList.remove('active');
        overlay.style.transition = 'none';
        overlay.style.opacity = '0';
        overlay.style.visibility = 'hidden';
        overlay.style.pointerEvents = 'none';
        overlay.style.background = 'transparent';
        overlay.style.backdropFilter = 'none';
        overlay.style.webkitBackdropFilter = 'none';
        if (overlay.parentNode) {
          overlay.remove();
        }
      }
      
      // Also check if body has any blur styles that need to be removed
      const body = document.body;
      if (body) {
        body.style.backdropFilter = '';
        body.style.webkitBackdropFilter = '';
        body.style.filter = '';
      }
    };

    // Clean up on route change
    cleanupOverlay();

    // Listen for logout events
    const handleLogout = () => {
      cleanupOverlay();
    };

    window.addEventListener('beforeunload', cleanupOverlay);
    window.addEventListener('authStateChanged', handleLogout);

    return () => {
      cleanupOverlay();
      window.removeEventListener('beforeunload', cleanupOverlay);
      window.removeEventListener('authStateChanged', handleLogout);
    };
  }, [location.pathname]);

  // Handle clicks outside dropdown and sidebar
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }

      if (helpMenuRef.current && !helpMenuRef.current.contains(event.target)) {
        setIsHelpMenuOpen(false);
      }

      // Close sidebar when clicking outside on mobile
      const sidebar = document.querySelector('.sidebar');
      if (sidebar && sidebar.classList.contains('open')) {
        if (!sidebar.contains(event.target) && !event.target.closest('.mobile-menu-btn') && !event.target.classList.contains('sidebar-overlay')) {
          closeSidebar();
        }
      }
    }

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef, helpMenuRef]);

  // Handle window resize for responsive button
  useEffect(() => {
    let resizeTimer = null;
    const handleResize = () => {
      // Debounce resize events to prevent excessive re-renders
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = setTimeout(() => {
        setWindowWidth(window.innerWidth);
        // Recalculate dropdown position on resize
        if (isOpen && notificationButtonRef.current && window.innerWidth <= 480) {
          const rect = notificationButtonRef.current.getBoundingClientRect();
          setDropdownTop(rect.bottom + 8);
        }
      }, 100);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
    };
  }, [isOpen]);

  // Show help tooltip after login
  useEffect(() => {
    const hasSeenHelpTooltip = localStorage.getItem('hasSeenHelpTooltip');
    const isAuthenticated = tokenService.isAuthenticated();

    if (!hasSeenHelpTooltip && isAuthenticated && helpButtonRef.current) {
      // Show tooltip after a short delay to ensure page is loaded
      const timer = setTimeout(() => {
        setShowHelpTooltip(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // Listen for auth state changes to show tooltip on login
  useEffect(() => {
    const handleAuthStateChange = () => {
      const hasSeenHelpTooltip = localStorage.getItem('hasSeenHelpTooltip');
      const isAuthenticated = tokenService.isAuthenticated();

      if (!hasSeenHelpTooltip && isAuthenticated && helpButtonRef.current) {
        // Clear any existing timeout
        if (authStateChangeTimeoutRef.current) {
          clearTimeout(authStateChangeTimeoutRef.current);
        }
        authStateChangeTimeoutRef.current = setTimeout(() => {
          setShowHelpTooltip(true);
          authStateChangeTimeoutRef.current = null;
        }, 2000);
      }
    };

    window.addEventListener('authStateChanged', handleAuthStateChange);
    return () => {
      window.removeEventListener('authStateChanged', handleAuthStateChange);
      // Clean up timeout on unmount
      if (authStateChangeTimeoutRef.current) {
        clearTimeout(authStateChangeTimeoutRef.current);
        authStateChangeTimeoutRef.current = null;
      }
    };
  }, []);

  const handleDismissHelpTooltip = useCallback(() => {
    setShowHelpTooltip(false);
    localStorage.setItem('hasSeenHelpTooltip', 'true');
  }, []);

  const handleStartPageTour = useCallback(() => {
    let tourId = getCurrentRouteTourId();

    if (tourId === 'disbursements-tour' && location.pathname === '/disbursements') {
      const isSingleTabActive = document.querySelector('[data-tour="disbursements-source-account"]') !== null;
      const isBulkTabActive = document.querySelector('[data-tour="disbursements-bulk-upload"]') !== null ||
        document.querySelector('[data-tour="disbursements-bulk-preview"]') !== null;

      if (isSingleTabActive) {
        tourId = 'disbursements-single-tour';
      } else if (isBulkTabActive) {
        tourId = 'disbursements-bulk-tour';
      } else {
        tourId = 'disbursements-single-tour';
      }

      logger.debug('Detected disbursements tab', { isSingleTabActive, isBulkTabActive, tourId });
    }

    if (location.pathname === '/settings' || location.pathname.startsWith('/settings/')) {
      const activeSections = {
        'settings-profile': 'settings-profile-tour',
        'settings-payment-methods': 'settings-payment-methods-tour',
        'settings-security': 'settings-security-tour',
        'settings-verification': 'settings-verification-tour',
        'settings-business': 'settings-business-tour',
        'settings-api': 'settings-api-tour',
        'settings-team': 'settings-team-tour',
      };

      const sectionIndicators = {
        'settings-profile-personal': 'settings-profile-tour',
        'settings-profile-address': 'settings-profile-tour',
        'settings-payment-tabs': 'settings-payment-methods-tour',
        'settings-payment-mobile': 'settings-payment-methods-tour',
        'settings-security-summary': 'settings-security-tour',
        'settings-security-pin': 'settings-security-tour',
        'settings-verification-levels': 'settings-verification-tour',
        'settings-business-info': 'settings-business-tour',
        'settings-api-generate': 'settings-api-tour',
        'settings-team-overview': 'settings-team-tour',
      };

      let foundSection = false;
      for (const [dataTourAttr, sectionTourId] of Object.entries(activeSections)) {
        if (document.querySelector(`[data-tour="${dataTourAttr}"]`) !== null) {
          tourId = sectionTourId;
          logger.debug('Detected settings section (wrapper)', { dataTourAttr, tourId });
          foundSection = true;
          break;
        }
      }

      if (!foundSection) {
        for (const [indicator, sectionTourId] of Object.entries(sectionIndicators)) {
          if (document.querySelector(`[data-tour="${indicator}"]`) !== null) {
            tourId = sectionTourId;
            logger.debug('Detected settings section (indicator)', { indicator, tourId });
            foundSection = true;
            break;
          }
        }
      }

      if (!foundSection) {
        logger.debug('No settings section detected, showing navigation tour');
        tourId = 'settings-tour';
      }
    }

    logger.debug('Starting tour for route', { tourId, pathname: location.pathname });
    if (tourId) {
      setTimeout(() => {
        startTour(tourId, true);
      }, 100);
    } else {
      logger.warn('No tour available for current route', { pathname: location.pathname });
    }
  }, [getCurrentRouteTourId, location.pathname, startTour]);

  const handleToggleHelpMenu = useCallback(() => {
    setIsHelpMenuOpen((open) => {
      const nextOpen = !open;
      if (nextOpen) {
        setIsOpen(false);
        if (helpButtonRef.current && window.innerWidth <= 480) {
          const rect = helpButtonRef.current.getBoundingClientRect();
          setHelpMenuTop(rect.bottom + 8);
        }
      }
      return nextOpen;
    });

    if (showHelpTooltip) {
      handleDismissHelpTooltip();
    }
  }, [handleDismissHelpTooltip, showHelpTooltip]);

  const handleHelpMenuStartTour = useCallback(() => {
    setIsHelpMenuOpen(false);
    handleStartPageTour();
  }, [handleStartPageTour]);

  // Auto-dismiss help tooltip after 10 seconds
  useEffect(() => {
    if (showHelpTooltip) {
      const autoDismissTimer = setTimeout(() => {
        handleDismissHelpTooltip();
      }, 10000); // 10 seconds

      return () => clearTimeout(autoDismissTimer);
    }
  }, [showHelpTooltip, handleDismissHelpTooltip]);

  // Calculate tooltip position for mobile
  useEffect(() => {
    if (showHelpTooltip && helpButtonRef.current && windowWidth <= 768) {
      const updatePosition = () => {
        if (helpButtonRef.current) {
          const buttonRect = helpButtonRef.current.getBoundingClientRect();
          const tooltipTop = buttonRect.bottom + 10;
          setTooltipPosition({
            top: tooltipTop,
            left: '50%'
          });
        }
      };

      updatePosition();

      let resizeTimer = null;
      let scrollTimer = null;
      
      const debouncedUpdatePosition = () => {
        if (resizeTimer) {clearTimeout(resizeTimer);}
        if (scrollTimer) {clearTimeout(scrollTimer);}
        
        resizeTimer = setTimeout(() => {
          updatePosition();
        }, 100);
      };

      const debouncedScrollUpdate = () => {
        if (scrollTimer) {clearTimeout(scrollTimer);}
        
        scrollTimer = setTimeout(() => {
          updatePosition();
        }, 50);
      };

      // Update position on scroll and resize with debouncing
      window.addEventListener('scroll', debouncedScrollUpdate, true);
      window.addEventListener('resize', debouncedUpdatePosition);

      return () => {
        window.removeEventListener('scroll', debouncedScrollUpdate, true);
        window.removeEventListener('resize', debouncedUpdatePosition);
        if (resizeTimer) {clearTimeout(resizeTimer);}
        if (scrollTimer) {clearTimeout(scrollTimer);}
      };
    }
  }, [showHelpTooltip, windowWidth]);

  // Calculate dropdown position on mobile when it opens
  useEffect(() => {
    if (isOpen && notificationButtonRef.current && windowWidth <= 480) {
      const rect = notificationButtonRef.current.getBoundingClientRect();
      setDropdownTop(rect.bottom + 8);
    } else {
      setDropdownTop(null);
    }
  }, [isOpen, windowWidth]);

  const toggleSidebar = () => {
    const sidebar = document.querySelector('.sidebar');
    const overlay = document.querySelector('.sidebar-overlay');

    if (sidebar) {
      const isOpen = sidebar.classList.contains('open');

      if (isOpen) {
        // Close sidebar
        closeSidebar();
      } else {
        // Clear any lingering inline styles before opening
        sidebar.style.transition = '';
        sidebar.style.left = '';
        sidebar.style.visibility = '';

        // Open sidebar
        sidebar.classList.add('open');
        setIsSidebarOpen(true);

        // Remove any existing overlay first
        if (overlay) {
          overlay.remove();
        }

        // Create overlay
        const newOverlay = document.createElement('div');
        newOverlay.className = 'sidebar-overlay';

        // Add overlay to body FIRST (before sidebar in DOM order)
        document.body.appendChild(newOverlay);

        // Trigger active state after a brief delay to ensure it's in the DOM
        setTimeout(() => {
          newOverlay.classList.add('active');
        }, 10);

        // Handle overlay clicks - close sidebar when clicking on overlay background
        const handleOverlayClick = (e) => {
          // Only close if clicking directly on overlay, not on sidebar
          const sidebar = document.querySelector('.sidebar');
          if (sidebar && sidebar.contains(e.target)) {
            // Click was on sidebar, don't close
            return;
          }

          // Click was on overlay background, close sidebar
          e.preventDefault();
          e.stopPropagation();
          closeSidebar();
        };

        // Use capture phase to ensure we catch the click before it bubbles
        newOverlay.addEventListener('click', handleOverlayClick, true);
        
        // Store reference for cleanup
        newOverlay._clickHandler = handleOverlayClick;
      }
    }
  };

  return (
    <div className="topbar">
      <div className="topbar-title-wrapper">
        {/* Mobile Menu Button */}
          <button
            className="mobile-menu-btn"
            onClick={toggleSidebar}
            type="button"
            aria-label="Toggle menu"
          >
            <span className="material-symbols-outlined" style={{ color: 'var(--text-primary)' }}>
              {isSidebarOpen ? 'close' : 'menu'}
            </span>
          </button>

        <div className="topbar-title-content">
          <div className="page-title">{computedTitle}</div>
          <div className="page-sub">
            <div className="greeting-text" style={{
              display: 'inline-flex',
              alignItems: 'center',
              color: 'var(--text-secondary)',
              fontWeight: 500,
              padding: '0',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              marginRight: windowWidth <= 480 ? '4px' : windowWidth <= 768 ? '6px' : '8px'
            }}>
              <span className="greeting-prefix">{t('greeting', { ns: 'common', name: '' }).split(',')[0]}, </span>
              <span style={{
                fontWeight: 600,
                color: 'var(--text-primary)',
                marginLeft: '3px'
              }}>
                {getUserFirstName(userData)}
              </span>
            </div>
            {userData?.usertype?.toLowerCase() === 'business_contact' ? (
              <button
                type="button"
                className="verification-badge"
                data-tour="dashboard-verification-level"
                onClick={() => navigate('/settings/business/verification')}
                aria-label={t('viewVerificationStatus', { ns: 'common', defaultValue: 'View verification status' })}
                style={{
                  background: (() => {
                    const level = businessVerification?.level ?? 0;
                    const colors = getVerificationLevelColors(level);
                    return colors.background;
                  })(),
                  color: (() => {
                    const level = businessVerification?.level ?? 0;
                    const colors = getVerificationLevelColors(level);
                    return colors.text;
                  })(),
                  border: (() => {
                    const level = businessVerification?.level ?? 0;
                    const colors = getVerificationLevelColors(level);
                    return `1px solid ${colors.border}`;
                  })(),
                  fontSize: windowWidth <= 480 ? 8 : windowWidth <= 768 ? 9 : 12,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: windowWidth <= 480 ? 2 : windowWidth <= 768 ? 2 : 6,
                  padding: windowWidth <= 480 ? '1px 3px' : windowWidth <= 768 ? '2px 4px' : '4px 12px',
                  borderRadius: windowWidth <= 480 ? '8px' : windowWidth <= 768 ? '10px' : '16px',
                  boxShadow: (() => {
                    const level = businessVerification?.level ?? 0;
                    const colors = getVerificationLevelColors(level);
                    return colors.shadow || '0 4px 10px rgba(15, 23, 42, 0.08)';
                  })(),
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  flexShrink: 1,
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textTransform: 'capitalize',
                  fontFamily: 'inherit'
                }}
                title={[
                  t('verificationTooltipTitle', { ns: 'settings' }),
                  t('verificationTooltipBusinessLevel', { ns: 'settings', level: verificationInfo.level ?? 0, tier: verificationInfo.tier ?? t('defaultBusinessTier', { ns: 'settings' }) }),
                  t('verificationTooltipStatus', { ns: 'settings', status: verificationInfo.status.label }),
                  t(
                    verificationInfo.status.key === 'verified'
                      ? 'verificationTooltipLimitsVerified'
                      : verificationInfo.status.key === 'pending'
                        ? 'verificationTooltipLimitsPending'
                        : 'verificationTooltipLimitsUnverified',
                    { ns: 'settings' }
                  )
                ].join('\n')}
              >
                <span className="material-symbols-outlined" style={{
                  fontSize: windowWidth <= 480 ? 10 : windowWidth <= 768 ? 12 : 16,
                  color: (() => {
                    const level = businessVerification?.level ?? 0;
                    const colors = getVerificationLevelColors(level);
                    return colors.icon ?? colors.text;
                  })(),
                  background: (() => {
                    const level = businessVerification?.level ?? 0;
                    const colors = getVerificationLevelColors(level);
                    return colors.iconBackground || (effectiveTheme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.25)');
                  })(),
                  padding: windowWidth <= 480 ? '2px' : windowWidth <= 768 ? '3px' : '6px',
                  borderRadius: '999px',
                  filter: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: effectiveTheme === 'dark'
                    ? 'inset 0 0 0 1px rgba(255,255,255,0.2)'
                    : 'inset 0 0 0 1px rgba(255,255,255,0.3)'
                }}>
                  {(() => {
                    const level = businessVerification?.level ?? 0;
                    return level >= 3 ? 'verified' : level >= 2 ? 'verified' : level === 1 ? 'pending' : 'error';
                  })()}
                </span>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600, letterSpacing: '0.03em' }}>
                  {verificationInfo.display}
                </span>
              </button>
            ) : (
              <button
                type="button"
                className="verification-badge"
                data-tour="dashboard-verification-level"
                onClick={() => navigate('/settings/individual/verification')}
                aria-label={t('viewVerificationStatus', { ns: 'common', defaultValue: 'View verification status' })}
                style={{
                  background: (() => {
                    const level = verificationLevel ?? 0;
                    const colors = getVerificationLevelColors(level);
                    return colors.background;
                  })(),
                  color: (() => {
                    const level = verificationLevel ?? 0;
                    const colors = getVerificationLevelColors(level);
                    return colors.text;
                  })(),
                  border: (() => {
                    const level = verificationLevel ?? 0;
                    const colors = getVerificationLevelColors(level);
                    return `1px solid ${colors.border}`;
                  })(),
                  fontSize: windowWidth <= 480 ? 8 : windowWidth <= 768 ? 9 : 12,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: windowWidth <= 480 ? 2 : windowWidth <= 768 ? 2 : 6,
                  padding: windowWidth <= 480 ? '1px 3px' : windowWidth <= 768 ? '2px 4px' : '4px 12px',
                  borderRadius: windowWidth <= 480 ? '8px' : windowWidth <= 768 ? '10px' : '16px',
                  boxShadow: (() => {
                    const level = verificationLevel ?? 0;
                    const colors = getVerificationLevelColors(level);
                    return colors.shadow || '0 4px 10px rgba(15, 23, 42, 0.08)';
                  })(),
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  flexShrink: 1,
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textTransform: 'capitalize',
                  fontFamily: 'inherit'
                }}
                title={[
                  t('verificationTooltipTitle', { ns: 'settings' }),
                  t('verificationTooltipLevel', { ns: 'settings', level: verificationInfo.level ?? 0, tier: verificationInfo.tierLabel }),
                  t('verificationTooltipStatus', { ns: 'settings', status: verificationInfo.status.label }),
                  t(
                    verificationInfo.status.key === 'verified'
                      ? 'verificationTooltipLimitsVerified'
                      : verificationInfo.status.key === 'pending'
                        ? 'verificationTooltipLimitsPending'
                        : 'verificationTooltipLimitsUnverified',
                    { ns: 'settings' }
                  ),
                  '',
                  t('verificationTooltipLevelsList', { ns: 'settings' })
                ].join('\n')}
              >
                <span className="material-symbols-outlined" style={{
                  fontSize: windowWidth <= 480 ? 10 : windowWidth <= 768 ? 12 : 16,
                  color: (() => {
                    const level = verificationLevel ?? 0;
                    const colors = getVerificationLevelColors(level);
                    return colors.icon ?? colors.text;
                  })(),
                  background: (() => {
                    const level = verificationLevel ?? 0;
                    const colors = getVerificationLevelColors(level);
                    return colors.iconBackground || (effectiveTheme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.25)');
                  })(),
                  padding: windowWidth <= 480 ? '2px' : windowWidth <= 768 ? '3px' : '6px',
                  borderRadius: '999px',
                  filter: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: effectiveTheme === 'dark'
                    ? 'inset 0 0 0 1px rgba(255,255,255,0.2)'
                    : 'inset 0 0 0 1px rgba(255,255,255,0.3)'
                }}>
                  {(() => {
                    const level = verificationLevel ?? 0;
                    return level >= 3 ? 'verified' : level >= 2 ? 'verified' : level === 1 ? 'pending' : 'error';
                  })()}
                </span>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600, letterSpacing: '0.03em' }}>
                  {verificationInfo.display}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="topbar-right">
        <input
          type="text"
          className="search"
          placeholder={computedSearch}
          value={searchTerm}
          onChange={(e) => {
            updateSearch(e.target.value);
            if (onSearchChange) {
              onSearchChange(e);
            }
          }}
        />

        <div className="topbar-actions">
          {/* Tour Guide Button */}
          <div style={{ position: 'relative' }} ref={helpMenuRef}>
            <button
              ref={helpButtonRef}
              className={`tour-btn ${showHelpTooltip ? 'help-button-pulse' : ''}`}
              aria-label={t('helpAndSupport', { ns: 'common', defaultValue: 'Help & Support' })}
              aria-expanded={isHelpMenuOpen}
              onClick={handleToggleHelpMenu}
              title={t('helpAndSupport', { ns: 'common', defaultValue: 'Help & Support' })}
              style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: windowWidth <= 480 ? '34px' : windowWidth <= 768 ? '36px' : '40px',
                height: windowWidth <= 480 ? '34px' : windowWidth <= 768 ? '36px' : '40px',
                borderRadius: '8px',
                border: 'none',
                background: showHelpTooltip
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.08) 100%)'
                  : 'var(--bg-input, #f3f4f6)',
                color: showHelpTooltip ? '#2563eb' : 'var(--text-primary, #000000)',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                padding: 0,
                boxShadow: showHelpTooltip
                  ? '0 0 0 2px rgba(59, 130, 246, 0.2), 0 4px 12px rgba(59, 130, 246, 0.15)'
                  : 'none'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = showHelpTooltip
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(59, 130, 246, 0.15) 100%)'
                  : 'var(--bg-hover, #e5e7eb)';
                e.target.style.transform = 'scale(1.08)';
                e.target.style.boxShadow = showHelpTooltip
                  ? '0 0 0 3px rgba(59, 130, 246, 0.3), 0 6px 16px rgba(59, 130, 246, 0.2)'
                  : '0 2px 8px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = showHelpTooltip
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.08) 100%)'
                  : 'var(--bg-input, #f3f4f6)';
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = showHelpTooltip
                  ? '0 0 0 2px rgba(59, 130, 246, 0.2), 0 4px 12px rgba(59, 130, 246, 0.15)'
                  : 'none';
              }}
            >
              <span className="material-symbols-outlined" style={{
                fontSize: windowWidth <= 480 ? 18 : windowWidth <= 768 ? 19 : 20,
                color: showHelpTooltip ? '#2563eb' : 'var(--text-primary)',
                transition: 'color 0.3s ease'
              }}>
                help_outline
              </span>
              {showHelpTooltip && (
                <span
                  className="help-button-badge"
                  style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: '#ef4444',
                    border: '2px solid var(--card)',
                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                  }}
                />
              )}
            </button>

            {showHelpTooltip && (
              <div 
                className="help-tooltip"
                style={{
                  // Ensure tooltip is visible on mobile by adjusting position
                  ...(windowWidth <= 768 && {
                    position: 'fixed',
                    top: `${tooltipPosition.top}px`,
                    left: tooltipPosition.left,
                    right: 'auto',
                    transform: 'translateX(-50%)',
                    zIndex: 10002,
                    width: windowWidth <= 480 ? 'calc(100vw - 24px)' : 'calc(100vw - 32px)',
                    maxWidth: windowWidth <= 480 ? 'calc(100vw - 24px)' : '320px',
                  })
                }}
              >
                <div 
                  className="help-tooltip__arrow"
                  style={{
                    ...(windowWidth <= 768 && {
                      right: '50%',
                      transform: 'translateX(50%) rotate(45deg)',
                    })
                  }}
                ></div>
                <div className="help-tooltip__content">
                  <p>{t('helpTooltipMessage', { ns: 'common', defaultValue: 'Click here for page tours and customer support' })}</p>
                  <button
                    className="help-tooltip__close"
                    onClick={handleDismissHelpTooltip}
                    aria-label={t('close', { ns: 'common' })}
                    style={{
                      ...(windowWidth <= 480 && {
                        width: '32px',
                        height: '32px',
                      })
                    }}
                  >
                    <span 
                      className="material-symbols-outlined"
                      style={{
                        fontSize: windowWidth <= 480 ? '22px' : '18px'
                      }}
                    >close</span>
                  </button>
                </div>
              </div>
            )}

            {isHelpMenuOpen && (
              <HelpSupportMenu
                onStartTour={handleHelpMenuStartTour}
                style={windowWidth <= 480 && helpMenuTop ? {
                  position: 'fixed',
                  right: '12px',
                  top: `${helpMenuTop}px`,
                  marginTop: 0,
                  zIndex: 10001,
                } : undefined}
              />
            )}
          </div>

          {/* Theme Toggle Button */}
          <button
            onClick={() => {
              // Toggle between light and dark (skip auto for quick toggle)
              const newTheme = effectiveTheme === 'dark' ? 'light' : 'dark';
              updateTheme(newTheme);
            }}
            aria-label={effectiveTheme === 'dark' 
              ? t('switchToLightMode', { ns: 'common', defaultValue: 'Switch to light mode' })
              : t('switchToDarkMode', { ns: 'common', defaultValue: 'Switch to dark mode' })
            }
            title={effectiveTheme === 'dark' 
              ? t('switchToLightMode', { ns: 'common', defaultValue: 'Switch to light mode' })
              : t('switchToDarkMode', { ns: 'common', defaultValue: 'Switch to dark mode' })
            }
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: windowWidth <= 480 ? '34px' : windowWidth <= 768 ? '36px' : '40px',
              height: windowWidth <= 480 ? '34px' : windowWidth <= 768 ? '36px' : '40px',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--bg-input, #f3f4f6)',
              color: 'var(--text-primary, #000000)',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              padding: 0,
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              if (window.matchMedia('(hover: hover)').matches) {
                e.target.style.background = 'var(--bg-hover, #e5e7eb)';
                e.target.style.transform = 'scale(1.08)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'var(--bg-input, #f3f4f6)';
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = 'none';
            }}
            onTouchStart={(e) => {
              e.currentTarget.style.transform = 'scale(0.95)';
            }}
            onTouchEnd={(e) => {
              setTimeout(() => {
                e.currentTarget.style.transform = 'scale(1)';
              }, 150);
            }}
          >
            <span className="material-symbols-outlined" style={{
              fontSize: windowWidth <= 480 ? 16 : windowWidth <= 768 ? 18 : 20,
              color: 'var(--text-primary)',
              transition: 'color 0.3s ease'
            }}>
              {effectiveTheme === 'dark' ? 'light_mode' : 'dark_mode'}
            </span>
          </button>

          <div className="dropdown-relative" ref={dropdownRef}>
            <button
              ref={notificationButtonRef}
              className="notification-btn"
              onClick={() => {
                setIsOpen((open) => !open);
                setIsHelpMenuOpen(false);
              }}
              aria-label={t('notificationsTitle', { ns: 'common', defaultValue: 'Notifications' })}
              aria-expanded={isOpen}
              style={{ color: 'var(--text-primary)' }}
            >
              <span className="material-symbols-outlined" style={{ color: 'var(--text-primary)' }}>notifications</span>
              {displayNotificationCount > 0 && (
                <span className="notification-badge">{displayNotificationCount}</span>
              )}
            </button>
            {isOpen && (
              <div
                className="dropdown-menu notification"
                style={windowWidth <= 480 && dropdownTop ? {
                  position: 'fixed',
                  right: '12px',
                  width: 'calc(100vw - 24px)',
                  maxWidth: 'calc(100vw - 24px)',
                  top: `${dropdownTop}px`
                } : {}}
              >
                <div className="notification-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                  <div className="notification-header-title" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span>{t('notificationsTitle', { ns: 'common', defaultValue: 'Notifications' })}</span>
                    {sortedNotifications.length > 0 && (
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {t('unreadCount', {
                          ns: 'common',
                          defaultValue: '{{count}} unread',
                          count: displayNotificationCount,
                        })}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button
                      onClick={handleRefreshNotifications}
                      className="notification-close-btn"
                      aria-label={t('refresh', { ns: 'common', defaultValue: 'Refresh notifications' })}
                      title={t('refresh', { ns: 'common', defaultValue: 'Refresh' })}
                      style={{ transform: 'none', opacity: isLoadingNotifications ? 0.6 : 1 }}
                      disabled={isLoadingNotifications}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>refresh</span>
                    </button>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="notification-close-btn"
                      aria-label={t('close', { ns: 'common', defaultValue: 'Close notifications' })}
                      title={t('close', { ns: 'common', defaultValue: 'Close' })}
                    >
                      <span className="material-symbols-outlined">close</span>
                    </button>
                  </div>
                </div>

                <div className="notification-list" style={{ maxHeight: '360px', overflowY: 'auto' }}>
                  {isLoadingNotifications && (
                    <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      {t('loadingNotifications', { ns: 'common', defaultValue: 'Loading notifications…' })}
                    </div>
                  )}

                  {!isLoadingNotifications && notificationError && (
                    <div style={{ padding: '24px', textAlign: 'center' }}>
                      <div style={{ color: 'var(--danger, #dc2626)', marginBottom: '12px' }}>{notificationError}</div>
                      <button
                        onClick={handleRefreshNotifications}
                        className="notification-view-btn"
                        style={{ margin: '0 auto' }}
                      >
                        {t('retry', { ns: 'common', defaultValue: 'Retry' })}
                      </button>
                    </div>
                  )}

                  {!isLoadingNotifications && !notificationError && !sortedNotifications.length && (
                    <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      {t('noNotificationsYet', { ns: 'common', defaultValue: 'No notifications yet.' })}
                    </div>
                  )}

                  {!isLoadingNotifications &&
                    !notificationError &&
                    sortedNotifications.map((notification, index) => {
                      const isActioning = actioningNotificationId === notification.id;
                      const isMarkAction = isActioning && notificationActionType === 'mark';
                      const isDeleteAction = isActioning && notificationActionType === 'delete';
                      const disableActions = isActioning || isClearingNotifications || isLoadingNotifications;
                      const iconGlyph = notification.isRead ? 'drafts' : 'mark_email_unread';
                      const iconColor = notification.isRead ? '#6b7280' : '#2563eb';
                      const iconBackground = notification.isRead ? 'rgba(107, 114, 128, 0.15)' : 'rgba(59, 130, 246, 0.15)';
                      const message =
                        notification.message ||
                        notification.code ||
                        t('notificationDefaultTitle', { ns: 'common', defaultValue: 'Notification' });

                      return (
                        <div
                          key={notification.id || `notification-${index}`}
                          className="notification-item"
                          style={{ opacity: notification.isRead ? 0.75 : 1 }}
                        >
                          <div className="notification-icon" style={{ background: iconBackground, color: iconColor }}>
                            <span className="material-symbols-outlined">{iconGlyph}</span>
                          </div>
                          <div className="notification-content">
                            <div className="notification-title" style={{ marginBottom: 4 }}>{message}</div>
                            <div className="notification-time">
                              {notification.formattedTime ||
                                t('justNow', { ns: 'common', defaultValue: 'Just now' })}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {!notification.isRead && (
                              <button
                                onClick={() => handleMarkNotificationRead(notification.id)}
                                disabled={disableActions}
                                className="notification-action-btn"
                                aria-label={t('markAsRead', { ns: 'common', defaultValue: 'Mark as read' })}
                                title={t('markAsRead', { ns: 'common', defaultValue: 'Mark as read' })}
                                style={{
                                  background: 'rgba(59, 130, 246, 0.12)',
                                  color: '#2563eb',
                                  cursor: disableActions ? 'not-allowed' : 'pointer',
                                  width: 32,
                                  height: 32,
                                  borderRadius: 10,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: 'none',
                                  transition: 'transform 0.15s ease',
                                }}
                              >
                                {isMarkAction ? (
                                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>hourglass_top</span>
                                ) : (
                                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>done_all</span>
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteNotification(notification.id)}
                              disabled={disableActions}
                              className="notification-action-btn"
                              aria-label={t('delete', { ns: 'common', defaultValue: 'Delete notification' })}
                              title={t('delete', { ns: 'common', defaultValue: 'Delete' })}
                              style={{
                                background: 'rgba(239, 68, 68, 0.12)',
                                color: '#b91c1c',
                                cursor: disableActions ? 'not-allowed' : 'pointer',
                                width: 32,
                                height: 32,
                                borderRadius: 10,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 'none',
                                transition: 'transform 0.15s ease',
                              }}
                            >
                              {isDeleteAction ? (
                                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>hourglass_top</span>
                              ) : (
                                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>

                <div className="notification-footer" style={{ justifyContent: 'space-between', gap: '8px' }}>
                  <button
                    onClick={handleClearNotifications}
                    className="notification-view-btn"
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--border-primary)',
                      color: 'var(--danger, #dc2626)',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      cursor:
                        isClearingNotifications || isLoadingNotifications || !sortedNotifications.length
                          ? 'not-allowed'
                          : 'pointer',
                      opacity:
                        isClearingNotifications || isLoadingNotifications || !sortedNotifications.length ? 0.5 : 1,
                    }}
                    disabled={isClearingNotifications || isLoadingNotifications || !sortedNotifications.length}
                  >
                    {isClearingNotifications
                      ? t('clearing', { ns: 'common', defaultValue: 'Clearing…' })
                      : t('clearAll', { ns: 'common', defaultValue: 'Clear all' })}
                  </button>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      // Close dropdown - notifications are already visible in the dropdown
                      // If a dedicated notifications page is added later, navigate to it here
                    }}
                    className="notification-view-all-btn"
                  >
                    {t('viewAll', { ns: 'common', defaultValue: 'View all' })}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="user-avatar-wrapper" style={{ display: 'flex', alignItems: 'center' }}>
            <button
              className="user-avatar"
              type="button"
              aria-label={t('openSettings', { ns: 'common', defaultValue: 'Open settings' })}
              style={{
                flexShrink: 0,
                width: windowWidth <= 480 ? '38px' : windowWidth <= 768 ? '42px' : '48px',
                height: windowWidth <= 480 ? '38px' : windowWidth <= 768 ? '42px' : '48px',
                borderRadius: '50%',
                background: (userData?.usertype?.toLowerCase() === 'business_contact' && businessLogoUrl) 
                  ? '#ffffff' 
                  : 'linear-gradient(135deg, var(--accent) 0%, var(--accent-hover, var(--accent)) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: windowWidth <= 480 ? '16px' : windowWidth <= 768 ? '18px' : '20px',
                fontWeight: '700',
                color: 'var(--text-inverse)',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: `2px solid ${effectiveTheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.3)'}`,
                boxShadow: effectiveTheme === 'dark'
                  ? '0 4px 12px rgba(0, 0, 0, 0.3), 0 0 0 0px rgba(59, 130, 246, 0.4)'
                  : '0 4px 12px rgba(0, 0, 0, 0.1), 0 0 0 0px rgba(59, 130, 246, 0.2)',
                position: 'relative',
                overflow: 'hidden',
                padding: 0
              }}
              onClick={() => navigate('/settings')}
              onMouseEnter={(e) => {
                if (window.matchMedia('(hover: hover)').matches) {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = effectiveTheme === 'dark'
                    ? '0 6px 20px rgba(0, 0, 0, 0.4), 0 0 0 4px rgba(59, 130, 246, 0.3)'
                    : '0 6px 20px rgba(0, 0, 0, 0.15), 0 0 0 4px rgba(59, 130, 246, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = effectiveTheme === 'dark'
                  ? '0 4px 12px rgba(0, 0, 0, 0.3), 0 0 0 0px rgba(59, 130, 246, 0.4)'
                  : '0 4px 12px rgba(0, 0, 0, 0.1), 0 0 0 0px rgba(59, 130, 246, 0.2)';
              }}
              onTouchStart={(e) => {
                e.currentTarget.style.transform = 'scale(0.95)';
              }}
              onTouchEnd={(e) => {
                setTimeout(() => {
                  e.currentTarget.style.transform = 'scale(1)';
                }, 150);
              }}
            >
              {/* Show business logo if available for business accounts, otherwise show initials */}
              {userData?.usertype?.toLowerCase() === 'business_contact' && businessLogoUrl ? (
                <img
                  src={businessLogoUrl}
                  alt="Business Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '50%'
                  }}
                  onError={() => {
                    // If image fails to load, remove it and show initials
                    setBusinessLogoUrl(null);
                    localStorage.removeItem('businessLogoUrl');
                  }}
                />
              ) : (
                <span style={{
                  position: 'relative',
                  zIndex: 1,
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                }}>
                  {getUserFirstName(userData).charAt(0).toUpperCase()}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;